import React, { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { useApp } from "@/context/AppContext";
import { uid } from "@/lib/api";

const EMPTY = { name: "", phone: "", specialty: "" };

// Add-consultant form shared by the Consultants page and Settings, so both write the same record.
// `compact` stacks the fields for narrow cards (Settings).
export function ConsultantForm({ className = "", compact = false, onSaved }) {
  const { save } = useApp();
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return toast.error("Name is required");
    const phone = form.phone.replace(/[^\d+]/g, "");
    if (phone && phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid mobile number");
    await save("consultants", { id: uid(), name, phone, specialty: form.specialty.trim() });
    setForm(EMPTY);
    toast.success("Consultant added");
    onSaved?.();
  };

  return (
    <form onSubmit={submit} className={`grid gap-4 ${compact ? "grid-cols-2" : "md:grid-cols-4 md:items-end md:gap-5"} ${className}`} data-testid="consultant-form">
      <div className={compact ? "col-span-2" : ""}>
        <p className="label-xs mb-1.5">Consultant name</p>
        <input data-testid="consultant-name-input" className="dm-input" placeholder="Dr. Anil Kumar" value={form.name} onChange={set("name")} required />
      </div>
      <div>
        <p className="label-xs mb-1.5">Mobile number</p>
        <input data-testid="consultant-phone-input" type="tel" inputMode="tel" className="dm-input font-mono" placeholder="98xxxxxxxx" value={form.phone} onChange={set("phone")} />
      </div>
      <div>
        <p className="label-xs mb-1.5">Specialty</p>
        <input data-testid="consultant-specialty-input" className="dm-input" placeholder="Endodontist" value={form.specialty} onChange={set("specialty")} />
      </div>
      <button type="submit" data-testid="consultant-add-btn" className={`dm-btn-primary ${compact ? "col-span-2" : ""}`}><Plus size={15} /> Add consultant</button>
    </form>
  );
}

// Delete → warning → type DELETE → confirm. The server archives (soft-deletes) the consultant,
// so past visits, fees and payouts keep pointing at a named record.
export function DeleteConsultantDialog({ consultant, onClose }) {
  const { remove } = useApp();
  const [typed, setTyped] = useState("");
  const [working, setWorking] = useState(false);
  const ok = typed.trim() === "DELETE";

  const close = () => { if (!working) { setTyped(""); onClose(); } };
  const run = async () => {
    if (!ok) return;
    setWorking(true);
    try {
      await remove("consultants", consultant.id);
      toast.success(`${consultant.name} archived`);
      setTyped("");
      onClose();
    } catch {
      toast.error("Could not delete consultant");
    } finally {
      setWorking(false);
    }
  };

  return (
    <AlertDialog open={!!consultant} onOpenChange={(open) => !open && close()}>
      <AlertDialogContent className="rounded-2xl border-slate-200 bg-white p-6 text-slate-900 shadow-xl sm:rounded-2xl" data-testid="delete-consultant-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-head text-lg font-semibold text-slate-900">Delete {consultant?.name}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-slate-600">
              <p>
                This consultant will be removed from your roster and from the visit form. Their past visits,
                fees and payouts are kept, and will still show their name in records and reports.
              </p>
              <p>You can restore them later from the Consultants page.</p>
              <div>
                <p className="label-xs mb-1.5">Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm</p>
                <input data-testid="delete-consultant-input" className="dm-input font-mono" autoComplete="off" autoFocus
                  value={typed} onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }} />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={working} className="rounded-full border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction data-testid="delete-consultant-confirm" disabled={!ok || working} onClick={(e) => { e.preventDefault(); run(); }}
            className="rounded-full bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-none hover:bg-red-700 disabled:opacity-40">
            {working ? <Loader2 size={15} className="animate-spin" /> : null} Delete consultant
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
